import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useI18n } from '../../i18n/I18nContext';
import { useUser } from '../../context/UserContext';
import agentService from '../../services/agentService';
import clientService from '../../services/clientService';
import {
    Container,
    Card,
    Button,
    Form,
    InputGroup,
    Badge,
    Row,
    Col,
    Alert,
    Spinner
} from 'react-bootstrap';
import { 
    FaRobot, 
    FaArrowLeft, 
    FaEnvelope, 
    FaCheckCircle, 
    FaInfoCircle,
    FaMagic,
    FaFilePdf,
    FaEye,
    FaSync
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import './AutomaticInvoice.css';

const AutomaticInvoice = () => {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { user } = useUser();
    
    const [proposedInvoices, setProposedInvoices] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [additionalEmail, setAdditionalEmail] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchClients = async () => {
        try {
            const data = await clientService.getAllClients();
            setClients(data);
        } catch (err) {
            console.error("Error fetching clients", err);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchPreviews = useCallback(async () => {
        setLoading(true);
        try {
            // Note: We no longer need to call agentService.login() 
            // as the Java backend handles authentication via our standard JWT token.
            
            const data = await agentService.getPendingPreviews();
            console.log("AI Agent Data Received:", data);
            
            // The Java bridge returns the agent's previews.
            // Mapping to UI structure:
            const mapped = (data || []).map(p => {
                const raw = p.raw || p; // Handle different wrapper levels if any
                
                // Find client in our local list for best practice (Company Name)
                const clientId = raw.client_id || raw.clientId;
                const dbClient = clients.find(c => c.id && clientId && c.id.toString() === clientId.toString());
                const finalClientName = dbClient ? (dbClient.entreprise || dbClient.nom) : (raw.client_name || raw.clientName || "Client");

                return {
                    id: p.id || raw.preview_id,
                    clientName: finalClientName,
                    status: p.status_label || raw.status || 'En attente',
                    statusRaw: (raw.status || '').toUpperCase(),
                    currency: raw.currency || 'MAD',
                    missions: (raw.mission_summaries || []).map(m => ({
                        id: m.mission_id,
                        ref: m.reference_number && !m.reference_number.startsWith('MS-') ? m.reference_number : null,
                        route: (m.expediteur_nom || m.expediteurNom) && (m.destinataire_nom || m.destinataireNom) 
                            ? `${m.expediteur_nom || m.expediteurNom} → ${m.destinataire_nom || m.destinataireNom}` 
                            : `${m.departure_location || '—'} → ${m.arrival_location || '—'}`,
                        price: m.preview_price || m.current_price,
                        currency: m.currency || 'MAD'
                    }))
                };
            });
            setProposedInvoices(mapped);
        } catch (error) {
            console.error('Error fetching agent previews:', error);
            const status = error.response?.status;
            const msg = error.response?.data?.message || error.message;
            toast.error(`Impossible de charger les suggestions : ${msg} ${status ? `(Code: ${status})` : ''}`);
        } finally {
            setLoading(false);
        }
    }, [clients]);

    useEffect(() => {
        fetchPreviews();
    }, [fetchPreviews, clients]);

    const handlePriceChange = async (invoiceId, missionId, newPrice) => {
        try {
            await agentService.updateMissionPrice(invoiceId, missionId, parseFloat(newPrice));
            toast.success("Prix mis à jour dans la suggestion.");
            // We could refresh here, or just trust the local update
        } catch (error) {
            toast.error("Erreur lors de la mise à jour du prix.");
        }
    };

    const handlePreviewPdf = async (invoiceId) => {
        try {
            toast.info("Génération de l'aperçu PDF...");
            const blob = await agentService.getPreviewPdfBlob(invoiceId);
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (error) {
            toast.error("Erreur lors de la génération du PDF.");
        }
    };

    const handleValidate = async (invoiceId) => {
        setIsProcessing(true);
        try {
            await agentService.createFinalInvoices(invoiceId, additionalEmail);
            toast.success("Facture générée avec succès ! Elle apparaîtra bientôt dans votre liste habituelle.");
            // Remove the invoice from the list
            setProposedInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
        } catch (error) {
            toast.error("Erreur lors de la génération de la facture.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleValidateAll = async () => {
        setIsProcessing(true);
        try {
            for (const inv of proposedInvoices) {
                await agentService.createFinalInvoices(inv.id, additionalEmail);
            }
            toast.success("Toutes les factures ont été générées !");
            setProposedInvoices([]);
        } catch (error) {
            toast.error("Une erreur est survenue lors de la validation globale.");
        } finally {
            setIsProcessing(false);
        }
    };

    const calculateInvoiceTotal = (invoice) => {
        return invoice.missions.reduce((sum, m) => sum + m.price, 0);
    };

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="automatic-invoice-container py-4">
                <Container>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <div className="d-flex align-items-center mb-1">
                                <h2 className="fw-bold text-primary mb-0">{t('billing.automaticInvoiceTitle')}</h2>
                                <Badge bg="info" className="ms-2 small opacity-75 d-flex align-items-center">
                                    IA
                                </Badge>
                            </div>
                            <p className="text-muted">
                                Révision des factures suggérées par l'intelligence artificielle. Ces données sont extraites en temps réel de l'agent de facturation.
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                className="rounded-pill px-4"
                                onClick={fetchPreviews}
                                disabled={loading}
                            >
                                <FaSync className={`me-2 ${loading ? 'fa-spin' : ''}`} /> Actualiser
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                className="rounded-pill px-4"
                                onClick={() => navigate('/billing')}
                            >
                                <FaArrowLeft className="me-2" /> {t('common.back')}
                            </Button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Connexion à l'agent via le backend...</p>
                        </div>
                    ) : proposedInvoices.length === 0 ? (
                        <Alert variant="info" className="text-center py-5 rounded-4 border-0 shadow-sm">
                            <FaInfoCircle size={40} className="mb-3 opacity-50" />
                            <h4>Aucune suggestion en attente</h4>
                            <p className="mb-0">Toutes les missions ont été traitées ou l'agent n'a pas encore généré de nouvelles propositions.</p>
                        </Alert>
                    ) : (
                        <Row>
                            <Col lg={8}>
                                {proposedInvoices.map((invoice) => (
                                    <Card key={invoice.id} className="glass-card border-0 mb-4 overflow-hidden">
                                        <Card.Header className="bg-white border-0 p-4 pb-0">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <h5 className="fw-bold mb-1">{invoice.clientName}</h5>
                                                    <small className="text-muted">AI Suggestion</small>
                                                </div>
                                                <div className="text-end">
                                                    <div className="h5 fw-bold text-primary mb-0">
                                                        {calculateInvoiceTotal(invoice).toFixed(2)} {invoice.currency}
                                                    </div>
                                                    <small className="text-muted">{invoice.missions.length} mission(s)</small>
                                                </div>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="p-4">
                                            <div className="bg-light rounded-4 p-3 mb-3">
                                                <h6 className="small fw-bold text-uppercase text-muted mb-3">Détail des missions & Prix HT</h6>
                                                {invoice.missions.map(mission => (
                                                    <Row key={mission.id} className="align-items-center mb-3 g-2">
                                                        <Col md={7}>
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-white rounded-circle p-2 me-3 border shadow-sm d-flex align-items-center justify-content-center" style={{width: '32px', height: '32px'}}>
                                                                    <FaMagic className="text-primary small" />
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold small">{mission.route}</div>
                                                                    {mission.ref && <div className="text-muted" style={{fontSize: '0.7rem'}}>Réf: {mission.ref}</div>}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        <Col md={5}>
                                                            <InputGroup size="sm" className="justify-content-end">
                                                                <Form.Control
                                                                    type="number"
                                                                    className="price-input text-end"
                                                                    defaultValue={mission.price}
                                                                    onBlur={(e) => handlePriceChange(invoice.id, mission.id, e.target.value)}
                                                                />
                                                                <InputGroup.Text className="bg-white border-2 border-start-0 fw-bold">
                                                                    {mission.currency}
                                                                </InputGroup.Text>
                                                            </InputGroup>
                                                        </Col>
                                                    </Row>
                                                ))}
                                            </div>
                                            
                                            <div className="d-flex justify-content-end gap-2 mt-4">
                                                <Button 
                                                    variant="outline-primary" 
                                                    className="rounded-pill px-4 d-flex align-items-center"
                                                    onClick={() => handlePreviewPdf(invoice.id)}
                                                >
                                                    <FaEye className="me-2" /> Prévisualiser PDF
                                                </Button>
                                                <Button 
                                                    variant="primary" 
                                                    className="rounded-pill px-4"
                                                    onClick={() => handleValidate(invoice.id)}
                                                    disabled={isProcessing}
                                                >
                                                    <FaCheckCircle className="me-2" /> Valider cette facture
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </Col>

                            <Col lg={4}>
                                <div className="sticky-top">
                                    <Card className="glass-card border-0 mb-4">
                                        <Card.Body className="p-4">
                                            <h5 className="fw-bold mb-3 d-flex align-items-center">
                                                <FaEnvelope className="me-2 text-primary" /> Envoi Automatique
                                            </h5>
                                            <p className="small text-muted mb-4">
                                                Les factures validées seront envoyées aux emails par défaut des clients. 
                                                Ajoutez une adresse pour recevoir une copie.
                                            </p>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="small fw-bold text-uppercase">Email de copie</Form.Label>
                                                <Form.Control 
                                                    type="email" 
                                                    placeholder="copie@transport.com" 
                                                    className="rounded-3 border-2"
                                                    value={additionalEmail}
                                                    onChange={(e) => setAdditionalEmail(e.target.value)}
                                                />
                                                <Form.Text className="text-muted" style={{fontSize: '0.7rem'}}>
                                                    Cette adresse ne sera pas enregistrée dans la base.
                                                </Form.Text>
                                            </Form.Group>

                                            <hr className="my-4" />

                                            <div className="d-grid gap-2">
                                                <Button 
                                                    variant="success" 
                                                    className="validate-btn py-3 d-flex align-items-center justify-content-center"
                                                    onClick={handleValidateAll}
                                                    disabled={isProcessing || proposedInvoices.length === 0}
                                                >
                                                    {isProcessing ? (
                                                        <>Traitement en cours...</>
                                                    ) : (
                                                        <><FaMagic className="me-2" /> Tout Valider & Générer</>
                                                    )}
                                                </Button>
                                                <Button variant="link" className="text-muted small" onClick={() => navigate('/billing')}>
                                                    Annuler et revenir à la liste
                                                </Button>
                                            </div>
                                            
                                            <Alert variant="warning" className="mt-4 border-0 rounded-4 small">
                                                <div className="d-flex">
                                                    <FaInfoCircle className="me-2 mt-1" />
                                                    <div>
                                                        Toute modification de prix ici sera transmise à l'agent pour la génération finale.
                                                    </div>
                                                </div>
                                            </Alert>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Container>
            </div>
        </DashboardLayout>
    );
};

export default AutomaticInvoice;
